/**
 * GroupedEventCard — Iter-310b — One parent card for a multi-role event.
 *
 * Used by both Client Dashboard (/client/shifts) and Admin Shifts
 * (/admin/shifts) so the chevron + localStorage memory + summary
 * metrics + per-role row layout stay consistent across the two
 * audiences. Per-role click handlers are passed in by the caller so
 * each surface can route to its own detail page.
 *
 * Props:
 *   group               — output of groupShiftsByEvent (isGroup=true)
 *   audience            — 'client' | 'admin' — drives copy + role-row links
 *   onOpenEvent(group)  — called when the parent title is clicked
 *   onOpenRole(role)    — called when a nested role row is clicked
 *   onOpenApplicants(role) — called when the per-role pending-count is clicked
 *   renderRoleExtras    — optional render-prop that gets the role row
 *                         (used by Admin to slot in Archive/Delete buttons)
 *
 * IMPORTANT — Grouping is display-only. Every nested role row exposes
 * its underlying `role.id` (the original shift_id) to the click
 * handlers. Applications, invitations, bookings, backups, waitlists
 * stay bound to that ID — see useGroupedEvents.test.js for the proof.
 */
import React from 'react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import {
  Calendar, Clock, MapPin, Users, ChevronDown, ChevronUp, Layers,
} from 'lucide-react';
import { formatTimeRange, formatDateShort } from '../utils/timeFormat';
import useLocalStorageState from '../hooks/useLocalStorageState';

function fmtPercent(num, denom) {
  if (!denom) return 0;
  return Math.round((num / denom) * 100);
}

const STATUS_TONES = {
  open: 'bg-amber-100 text-amber-800',
  filled: 'bg-emerald-100 text-emerald-800',
  cancelled: 'bg-gray-100 text-gray-600',
  completed: 'bg-blue-100 text-blue-700',
};

export default function GroupedEventCard({
  group,
  audience = 'client',
  defaultExpanded = true,
  onOpenEvent,
  onOpenRole,
  onOpenApplicants,
  renderRoleExtras,
}) {
  const storageKey = `iEvntur:group-expanded:${audience}:${group.event_group_id || group.key}`;
  const [expanded, setExpanded] = useLocalStorageState(storageKey, defaultExpanded);

  // Summary metrics (Iter-310b).
  const totalPositions = group.total_talent_needed || 0;
  const filled = group.total_confirmed || 0;
  const pending = group.total_pending || 0;
  const open = Math.max(0, totalPositions - filled);
  const fillPct = fmtPercent(filled, totalPositions);

  const headerTitle = group.event_title || group.venue_name || 'Multi-Role Event';

  return (
    <Card
      className="border-cyan-200 bg-gradient-to-br from-white to-cyan-50/30"
      data-testid={`event-group-${group.event_group_id}`}
    >
      <CardContent className="p-4 sm:p-5">
        <div className="flex items-start justify-between gap-2">
          <button
            type="button"
            onClick={() => onOpenEvent && onOpenEvent(group)}
            className="text-left flex-1 min-w-0"
            data-testid={`event-group-${group.event_group_id}-title-btn`}
          >
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <h3 className="text-base sm:text-lg font-bold text-gray-900 hover:text-cyan-700 break-words">
                {headerTitle}
              </h3>
              <Badge
                className="bg-cyan-600 text-white border-cyan-700 text-xs"
                data-testid={`event-group-${group.event_group_id}-roles-badge`}
              >
                {group.roles_count} {group.roles_count === 1 ? 'Role' : 'Roles'}
              </Badge>
              {group.status && (
                <Badge className={`${STATUS_TONES[group.status] || 'bg-gray-100 text-gray-700'} text-xs`}>
                  {group.status}
                </Badge>
              )}
              {group.series_total > 1 && (
                <Badge className="bg-indigo-100 text-indigo-700 border border-indigo-200 text-xs gap-1" data-testid={`event-group-${group.event_group_id}-series-badge`}>
                  <Layers className="w-3 h-3" /> Day {group.series_day_index} of {group.series_total}
                </Badge>
              )}
            </div>
            <div className="text-xs sm:text-sm text-gray-600 flex items-center gap-3 flex-wrap">
              <span className="inline-flex items-center gap-1"><Calendar className="w-3.5 h-3.5" />{formatDateShort(group.shift_date)}</span>
              <span className="inline-flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{group.city}, {group.state}</span>
              <span className="inline-flex items-center gap-1"><Clock className="w-3.5 h-3.5" />{formatTimeRange(group.start_time, group.end_time)}</span>
              {audience === 'admin' && group.client_display && (
                <span className="text-gray-500">· {group.client_display}</span>
              )}
            </div>
            {group.series_total > 1 && group.series_dates && (
              <div className="mt-1.5 flex items-center gap-1.5 flex-wrap" data-testid={`event-group-${group.event_group_id}-series-dates`}>
                <Layers className="w-3 h-3 text-indigo-400 shrink-0" />
                <span className="text-[11px] text-gray-400 mr-0.5">Multi-day series:</span>
                {group.series_dates.map((d) => (
                  <span
                    key={d.shift_id}
                    className={d.is_current
                      ? 'text-[11px] font-semibold bg-indigo-600 text-white rounded px-1.5 py-0.5'
                      : 'text-[11px] bg-indigo-50 text-indigo-700 border border-indigo-200 rounded px-1.5 py-0.5'}
                  >
                    {formatDateShort(d.shift_date)}
                  </span>
                ))}
              </div>
            )}
          </button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="shrink-0 h-8 px-2 border-cyan-200 text-cyan-700 hover:bg-cyan-50"
            onClick={() => setExpanded(!expanded)}
            data-testid={`event-group-${group.event_group_id}-toggle`}
            aria-label={expanded ? 'Hide roles' : 'Show roles'}
          >
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            <span className="ml-1 text-xs font-medium">{expanded ? 'Hide' : 'Show'} Roles</span>
          </Button>
        </div>

        {/* ── Iter-310b — Event-level summary metrics ───────────────── */}
        <div
          className="grid grid-cols-2 sm:grid-cols-5 gap-2 mt-3"
          data-testid={`event-group-${group.event_group_id}-summary`}
        >
          <Metric label="Total" value={totalPositions} tone="slate" testid={`${group.event_group_id}-total`} />
          <Metric label="Filled" value={filled} tone="emerald" testid={`${group.event_group_id}-filled`} />
          <Metric label="Pending" value={pending} tone="indigo" testid={`${group.event_group_id}-pending`} />
          <Metric label="Open" value={open} tone="amber" testid={`${group.event_group_id}-open`} />
          <Metric label="Fill" value={`${fillPct}%`} tone={fillPct >= 100 ? 'emerald' : fillPct >= 50 ? 'amber' : 'rose'} testid={`${group.event_group_id}-fillpct`} />
        </div>

        {/* ── Nested role rows ─────────────────────────────────────── */}
        {expanded && (
          <div className="mt-4 border-t border-cyan-100 pt-3 space-y-2" data-testid={`event-group-${group.event_group_id}-roles`}>
            {group.roles.map((r) => {
              // Per-role metrics (each role keeps its own shift_id).
              const rFilled = r.confirmed_count ?? r.staffed_count ?? r.bookings_count ?? 0;
              const rNeeded = r.talent_needed || 0;
              const rPending = r.applicant_count ?? r.pending_applications ?? 0;
              return (
                <div
                  key={r.id}
                  className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg bg-white border border-gray-100 hover:border-cyan-300 hover:bg-cyan-50/30 cursor-pointer transition-colors"
                  onClick={() => onOpenRole && onOpenRole(r)}
                  data-testid={`event-group-${group.event_group_id}-role-${r.id}`}
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-gray-900">{r.role_name}</p>
                    <p className="text-[11px] text-gray-500">
                      {formatTimeRange(r.start_time, r.end_time)}
                      {r.tier_required === 'premium' && <span className="ml-2 text-purple-700">· Premium</span>}
                    </p>
                  </div>
                  <div className="flex items-center gap-3 sm:gap-4 text-xs flex-wrap">
                    <span className="inline-flex items-center gap-1 text-gray-700">
                      <Users className="w-3.5 h-3.5" />
                      <strong>{rFilled}/{rNeeded}</strong> filled
                    </span>
                    {rPending > 0 && (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); onOpenApplicants && onOpenApplicants(r); }}
                        className="text-indigo-700 font-medium hover:underline"
                        data-testid={`open-applicants-${r.id}`}
                      >
                        {rPending} pending
                      </button>
                    )}
                    {(r.backups_needed || 0) > 0 && (
                      <span
                        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded border border-amber-300 bg-amber-50 text-amber-800 font-semibold"
                        title={`${r.backup_count || 0} / ${r.backups_needed} backups`}
                      >
                        B {r.backup_count || 0}/{r.backups_needed}
                      </span>
                    )}
                    {r.bill_rate != null && (
                      <span className="text-emerald-700 font-semibold">${Number(r.bill_rate).toFixed(2)}/hr</span>
                    )}
                    {renderRoleExtras && renderRoleExtras(r)}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function Metric({ label, value, tone = 'slate', testid }) {
  const tones = {
    slate:   'bg-slate-50   text-slate-700 border-slate-200',
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    indigo:  'bg-indigo-50  text-indigo-700  border-indigo-200',
    amber:   'bg-amber-50   text-amber-700   border-amber-200',
    rose:    'bg-rose-50    text-rose-700    border-rose-200',
  };
  return (
    <div
      className={`rounded-lg border px-2.5 py-1.5 text-center ${tones[tone] || tones.slate}`}
      data-testid={`metric-${testid}`}
    >
      <div className="text-[10px] uppercase tracking-wide font-semibold opacity-70">{label}</div>
      <div className="text-base font-bold leading-snug">{value}</div>
    </div>
  );
}
